/**
 * Titel Generator — Generate titles using After Effects templates per client
 * Supports render queue: add multiple items, render them all at once
 * Stitch design system
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Autocomplete, LinearProgress, Dialog, Tooltip } from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import PageHeader from '../../ui-components/PageHeader';
import toast from 'react-hot-toast';

/* ── Stitch tokens ─────────────────────────────────────────────────────────── */
const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  violet: '#7c3aed', violetBg: '#f5f3ff', violetBorder: '#ddd6fe',
  bg: '#f8f9fa', surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  error: '#a83836', errorBg: '#fef2f2',
  amber: '#f59e0b', amberBg: '#fef3c7',
  pink: '#ec4899', pinkBg: '#fce7f3',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const IconBadge = ({ name, size = 48, iconSize = 26, bg = T.primaryContainer, color = T.primary }) => (
  <Box sx={{ width: size, height: size, borderRadius: '0.75rem', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
    <Icon name={name} size={iconSize} sx={{ color }} />
  </Box>
);

const Badge = ({ label, color = T.primary, bg = T.primaryContainer, sx = {} }) => (
  <Typography component="span" sx={{
    fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700,
    color, bgcolor: bg, px: 1.25, py: 0.3, borderRadius: '9999px',
    display: 'inline-block', textTransform: 'uppercase', letterSpacing: '0.06em', ...sx,
  }}>
    {label}
  </Typography>
);

const SectionLabel = ({ children }) => (
  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
    {children}
  </Typography>
);

/* Stitch-styled pill selector */
const PillSelect = ({ options, value, onChange, multi = false, disabled = false }) => (
  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
    {options.map(opt => {
      const selected = multi ? value.includes(opt.value) : value === opt.value;
      return (
        <Box
          key={opt.value}
          onClick={!disabled ? () => onChange(opt.value) : undefined}
          sx={{
            px: 1.5, py: 0.5, borderRadius: '9999px', cursor: disabled ? 'default' : 'pointer',
            bgcolor: selected ? T.primaryContainer : T.surfaceLow,
            border: `1.5px solid ${selected ? `${T.primary}40` : 'transparent'}`,
            opacity: disabled ? 0.5 : 1,
            transition: 'all 0.15s',
            '&:hover': !disabled && !selected ? { bgcolor: T.surfaceHigh } : {},
          }}
        >
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: selected ? 700 : 500, color: selected ? T.primary : T.text }}>
            {opt.label}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

/* Gradient button */
const GradientButton = ({ children, onClick, disabled, icon, variant = 'primary', fullWidth, sx: sxOverride = {} }) => {
  const gradients = {
    primary: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`,
    violet: `linear-gradient(135deg, ${T.violet} 0%, #6d28d9 100%)`,
    emerald: `linear-gradient(135deg, ${T.emerald} 0%, #059669 100%)`,
  };
  return (
    <Box
      onClick={!disabled ? onClick : undefined}
      sx={{
        px: 2.5, py: 1.25, borderRadius: '0.75rem',
        cursor: !disabled ? 'pointer' : 'default',
        background: !disabled ? gradients[variant] : T.surfaceHigh,
        color: !disabled ? '#fff' : T.outlineVariant,
        fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.85rem',
        boxShadow: !disabled ? '0 4px 12px rgba(99,89,131,0.25)' : 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
        transition: 'all 0.2s',
        ...(fullWidth && { width: '100%' }),
        '&:hover': !disabled ? { boxShadow: '0 6px 20px rgba(99,89,131,0.35)', transform: 'translateY(-1px)' } : {},
        ...sxOverride,
      }}
    >
      {icon && <Icon name={icon} size={18} sx={{ color: 'inherit' }} />}
      {children}
    </Box>
  );
};

/* CSS spinner */
const Spinner = ({ size = 18 }) => (
  <Box sx={{
    width: size, height: size, border: '2px solid rgba(255,255,255,0.3)',
    borderTop: '2px solid #fff', borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
  }} />
);

/* ── Template type definitions ─────────────────────────────────────────────── */
const TEMPLATE_TYPES = [
  { key: 'Naamtitel', label: 'Naamtitel', icon: 'person', fields: ['name', 'functionTitle'] },
  { key: 'Titel', label: 'Titel', icon: 'title', fields: ['title'] },
  { key: 'Intro', label: 'Intro', icon: 'play_circle', fields: [] },
  { key: 'Outro', label: 'Outro', icon: 'stop_circle', fields: [] },
];

let queueIdCounter = 0;
const generateQueueId = () => `q_${++queueIdCounter}_${Date.now()}`;

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function NameTitleGenerator() {
  const navigate = useNavigate();

  // Client selection
  const [klanten, setKlanten] = useState([]);
  const [loadingKlanten, setLoadingKlanten] = useState(false);
  const [selectedKlant, setSelectedKlant] = useState(null);

  // Huisstijl / templates
  const [huisstijl, setHuisstijl] = useState([]);
  const [loadingHuisstijl, setLoadingHuisstijl] = useState(false);
  const [aeTemplates, setAeTemplates] = useState({});

  // Selected template type and specific template
  const [selectedType, setSelectedType] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Form fields
  const [name, setName] = useState('');
  const [functionTitle, setFunctionTitle] = useState('');
  const [titleText, setTitleText] = useState('');
  const [subtitleText, setSubtitleText] = useState('');
  const [urlText, setUrlText] = useState('');
  const [position, setPosition] = useState('L');
  const [metFunctie, setMetFunctie] = useState(true);

  // Format selection
  const [selectedFormats, setSelectedFormats] = useState([]);

  // Worker status
  const [workerOnline, setWorkerOnline] = useState(null);
  const [onlineWorkers, setOnlineWorkers] = useState([]);

  // Queue state
  const [queue, setQueue] = useState([]);
  const [isRendering, setIsRendering] = useState(false);
  const [error, setError] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Direct MOV download state
  const [isDownloading, setIsDownloading] = useState(false);

  // Preview lightbox
  const [lightboxUrl, setLightboxUrl] = useState(null);

  // Refs
  const pollingRef = useRef(null);
  const elapsedRef = useRef(null);
  const nameInputRef = useRef(null);
  const titleInputRef = useRef(null);
  const queueRef = useRef(queue);
  queueRef.current = queue;

  // Available formats from selected template
  const availableFormats = (() => {
    if (!selectedTemplate) return [];
    const cfgs = selectedTemplate.templateConfigs || [];
    if (cfgs.length > 0 && selectedType) {
      const matching = cfgs.filter(c => c.fields?.Type === selectedType.key);
      return [...new Set(matching.map(c => c.fields?.Formaat).filter(Boolean))];
    }
    return [];
  })();

  // Available fields from selected template
  const availableFieldsForCurrentSelection = (() => {
    if (!selectedTemplate || !selectedType) return selectedTemplate?.availableFields || [];
    const cfgs = selectedTemplate.templateConfigs || [];
    if (cfgs.length > 0) {
      const matching = cfgs.filter(c => c.fields?.Type === selectedType.key);
      const allVelden = new Set();
      matching.forEach(c => (c.fields?.Velden || []).forEach(v => allVelden.add(v)));
      return [...allVelden];
    }
    return selectedTemplate?.availableFields || [];
  })();

  const findMatchingConfig = (typeKey, format) => {
    if (!selectedTemplate) return null;
    const cfgs = selectedTemplate.templateConfigs || [];
    return cfgs.find(c => c.fields?.Type === typeKey && c.fields?.Formaat === format) || null;
  };

  // Check if position is locked by configuratie
  const configPosition = (() => {
    if (!selectedTemplate || !selectedType || selectedType.key !== 'Naamtitel') return null;
    const cfgs = selectedTemplate.templateConfigs || [];
    const matching = cfgs.filter(c => c.fields?.Type === 'Naamtitel');
    if (matching.length === 0) return null;
    const positions = [...new Set(matching.map(c => c.fields?.Positie).filter(Boolean))];
    if (positions.length === 1 && positions[0] !== 'Beide') return positions[0];
    return null;
  })();

  useEffect(() => { setSelectedFormats([]); }, [selectedTemplate?.id]);
  // Ctrl+Z / Cmd+Z buiten tekstvelden = laatste actie terugdraaien
  useEffect(() => registreerOngedaanSneltoets(), []);
  useEffect(() => { if (configPosition) setPosition(configPosition); }, [configPosition]);

  // Derived state
  const allCompleted = queue.length > 0 && queue.every(item => item.status === 'completed');
  const allDone = queue.length > 0 && queue.every(item => item.status === 'completed' || item.status === 'error');
  const hasCompletedItems = queue.some(item => item.status === 'completed');

  useEffect(() => { fetchKlanten(); checkWorkerStatus(); }, []);
  useEffect(() => { return () => { if (pollingRef.current) clearInterval(pollingRef.current); if (elapsedRef.current) clearInterval(elapsedRef.current); }; }, []);
  useEffect(() => { if (selectedKlant) fetchHuisstijl(selectedKlant.id); else { setHuisstijl([]); setAeTemplates({}); setSelectedType(null); setSelectedTemplate(null); } }, [selectedKlant]);
  useEffect(() => {
    if (isRendering && allDone) {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
      setIsRendering(false);
      if (allCompleted) toast.success(`Alle ${queue.length} items succesvol gerenderd!`);
      else toast.error(`${queue.filter(i => i.status === 'error').length} item(s) mislukt`);
    }
  }, [isRendering, allDone, allCompleted, queue]);

  /* ── Data fetching ─────────────────────────────────────────────────────── */
  const fetchKlanten = async () => {
    setLoadingKlanten(true);
    try {
      const [klantRes, aeRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/Api/klanten`),
        authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/accounts-with-type?type=${encodeURIComponent('After Effects')}`),
      ]);
      const data = await klantRes.json();
      if (!klantRes.ok) throw new Error(data.error || 'Klanten ophalen mislukt');
      const all = data.klanten || [];
      let filtered = all;
      if (aeRes.ok) {
        const aeData = await aeRes.json();
        const allowed = new Set(aeData.account_airtable_ids || []);
        filtered = all.filter(k => allowed.has(k.id));
      } else {
        console.warn('[TitelGen] AE-account filter unavailable; showing all klanten');
      }
      setKlanten(filtered);
    } catch (err) { console.error('Error fetching klanten:', err); toast.error('Klanten ophalen mislukt'); }
    finally { setLoadingKlanten(false); }
  };

  const checkWorkerStatus = async () => {
    setWorkerOnline(null);
    try {
      const [res] = await Promise.all([authFetch(`${BACKEND_URL}/Api/name-title/worker-status`), new Promise(r => setTimeout(r, 2000))]);
      const data = await res.json();
      setWorkerOnline(data.online);
      setOnlineWorkers(data.workers || []);
    } catch { setWorkerOnline(false); setOnlineWorkers([]); }
  };

  const fetchHuisstijl = async (klantId) => {
    setLoadingHuisstijl(true);
    setAeTemplates({});
    setSelectedType(null);
    setSelectedTemplate(null);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/klanten/${klantId}/huisstijl`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Huisstijl ophalen mislukt');
      const records = data.huisstijl || [];
      setHuisstijl(records);
      const aeMap = {};
      records.filter(r => r.fields?.Type === 'After Effects').forEach(r => {
        const attachments = r.fields?.Bestand;
        const filename = (Array.isArray(attachments) && attachments.length > 0) ? (attachments[0].filename || '').toLowerCase() : '';
        r.isDirectMov = filename.endsWith('.mov');
        r.linkedFontIds = r.fields?.['Gekoppelde fonts'] || [];
        const cfgs = r.configuraties || [];
        if (cfgs.length > 0) {
          r.templateConfigs = cfgs;
          const allVelden = new Set();
          cfgs.forEach(c => (c.fields?.Velden || []).forEach(v => allVelden.add(v)));
          r.availableFields = [...allVelden];
          const configTypes = new Set();
          cfgs.forEach(c => { if (c.fields?.Type) configTypes.add(c.fields.Type); });
          configTypes.forEach(aeType => { if (!aeMap[aeType]) aeMap[aeType] = []; if (!aeMap[aeType].includes(r)) aeMap[aeType].push(r); });
        } else { r.templateConfigs = []; r.availableFields = []; }
      });
      setAeTemplates(aeMap);
    } catch (err) { console.error('Error fetching huisstijl:', err); toast.error('Huisstijl ophalen mislukt'); }
    finally { setLoadingHuisstijl(false); }
  };

  /* ── Queue actions ─────────────────────────────────────────────────────── */
  const handleAddToQueue = () => {
    if (!selectedType || !selectedTemplate) return;
    if (!selectedTemplate?.isDirectMov) {
      const af = availableFieldsForCurrentSelection;
      if (af.includes('Naam') && !name.trim()) { setError('Vul een naam in'); return; }
    }
    if (selectedFormats.length === 0) { setError('Selecteer minimaal één formaat'); return; }

    const newItems = selectedFormats.map(format => {
      const matchingCfg = findMatchingConfig(selectedType.key, format);
      const cfgPositie = matchingCfg?.fields?.Positie;
      const compNameOverride = (cfgPositie === 'Beide') ? undefined : (matchingCfg?.fields?.Composititenaam || undefined);
      return {
        id: generateQueueId(), templateType: selectedType.key, format,
        name: selectedType.key === 'Naamtitel' ? name.trim() : '',
        functionTitle: selectedType.key === 'Naamtitel' && metFunctie ? functionTitle.trim() : '',
        position: selectedType.key === 'Naamtitel' ? position : undefined,
        metFunctie: selectedType.key === 'Naamtitel' ? metFunctie : undefined,
        title: selectedType.key !== 'Naamtitel' ? titleText.trim() : '',
        subtitle: selectedType.key !== 'Naamtitel' ? subtitleText.trim() : '',
        url: selectedType.key !== 'Naamtitel' ? urlText.trim() : '',
        isDirectMov: selectedTemplate?.isDirectMov || false,
        compNameOverride,
        jobId: null, status: 'queued', progress: 0, progressMessage: '',
      };
    });

    setQueue(prev => [...prev, ...newItems]);
    setError(null);
    if (selectedType.key === 'Naamtitel') { setName(''); setFunctionTitle(''); setTimeout(() => nameInputRef.current?.focus(), 50); }
    else { setTitleText(''); setSubtitleText(''); setUrlText(''); setTimeout(() => titleInputRef.current?.focus(), 50); }
    toast.success(`${newItems.length > 1 ? `${newItems.length} items` : '1 item'} toegevoegd aan queue`);
  };

  const handleRemoveFromQueue = (itemId) => {
    const index = queue.findIndex(item => item.id === itemId);
    const verwijderd = queue[index];
    setQueue(prev => prev.filter(item => item.id !== itemId));
    if (verwijderd) {
      // Ongedaan maken = item op dezelfde plek terugzetten in de wachtrij
      biedOngedaanAan('Item uit wachtrij gehaald', async () => {
        setQueue(prev => {
          if (prev.some(item => item.id === itemId)) return prev;
          const copy = [...prev];
          copy.splice(Math.min(index, copy.length), 0, verwijderd);
          return copy;
        });
      });
    }
  };

  const getItemLabel = (item) => {
    const fmt = item.format ? ` [${item.format}]` : '';
    if (item.templateType === 'Naamtitel') {
      const pos = item.position === 'R' ? 'R' : 'L';
      const label = item.metFunctie ? `${item.name} · ${item.functionTitle}` : item.name;
      return `${label} (${pos})${fmt}`;
    }
    if (['Titel', 'Intro', 'Outro'].includes(item.templateType)) {
      const parts = [item.title, item.subtitle].filter(Boolean);
      if (parts.length > 0) return `${parts.join(' · ')}${fmt}`;
    }
    return `${item.templateType}${fmt}`;
  };

  /* ── Polling ───────────────────────────────────────────────────────────── */
  const pollAllJobs = useCallback(async (currentQueue) => {
    const activeItems = currentQueue.filter(item => item.jobId && (item.status === 'pending' || item.status === 'rendering'));
    if (activeItems.length === 0) return;
    const updates = {};
    await Promise.all(activeItems.map(async (item) => {
      try {
        const res = await authFetch(`${BACKEND_URL}/Api/name-title/status/${item.jobId}`);
        const data = await res.json();
        if (!res.ok) return;
        const newStatus = data.status === 'completed' ? 'completed' : (data.status === 'error' || data.status === 'failed') ? 'error' : data.status === 'rendering' ? 'rendering' : 'pending';
        updates[item.id] = { status: newStatus, progress: data.progress || 0, progressMessage: data.message || '', wachtOpAe: data.wachtOpAe || null, ...(newStatus === 'error' ? { errorMessage: data.error || data.message || 'Render mislukt' } : {}) };
      } catch (err) { console.error(`Poll error for ${item.id}:`, err); }
    }));
    if (Object.keys(updates).length > 0) setQueue(prev => prev.map(item => updates[item.id] ? { ...item, ...updates[item.id] } : item));
  }, []);

  /* ── Render actions ────────────────────────────────────────────────────── */
  const handleStartRendering = async () => {
    if (queue.length === 0 || !selectedTemplate || !selectedKlant) return;
    setIsRendering(true);
    setError(null);
    setElapsedSeconds(0);
    elapsedRef.current = setInterval(() => setElapsedSeconds(s => s + 1), 1000);

    const updatedQueue = [...queue];
    for (let i = 0; i < updatedQueue.length; i++) {
      const item = updatedQueue[i];
      if (item.status !== 'queued') continue;
      try {
        const body = { templateType: item.templateType, huisstijlElementId: selectedTemplate.id, format: item.format, klantName: selectedKlant?.fields?.Account || '', fontElementIds: selectedTemplate.linkedFontIds || [], accountAirtableId: selectedKlant?.id || null, referrerPath: window.location.pathname + window.location.search };
        if (item.compNameOverride) body.compNameOverride = item.compNameOverride;
        if (item.templateType === 'Naamtitel') { body.name = item.name; body.position = item.position || 'L'; body.metFunctie = item.metFunctie !== false; if (item.metFunctie !== false) body.functionTitle = item.functionTitle; }
        else { if (item.title) body.title = item.title; if (item.subtitle) body.subtitle = item.subtitle; if (item.url) body.url = item.url; }
        const res = await authFetch(`${BACKEND_URL}/Api/name-title/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Genereren mislukt');
        if (data.isDirectMov) updatedQueue[i] = { ...item, jobId: data.jobId, status: 'completed', progress: 100, progressMessage: 'Voltooid!' };
        else updatedQueue[i] = { ...item, jobId: data.jobId, status: 'pending', progressMessage: 'Wachten op render server...' };
      } catch (err) { updatedQueue[i] = { ...item, status: 'error', errorMessage: err.message }; }
      setQueue([...updatedQueue]);
    }
    pollingRef.current = setInterval(() => pollAllJobs(queueRef.current), 1000);
  };

  const handleDownloadItem = async (item) => {
    if (!item.jobId) return;
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/name-title/download/${item.jobId}`);
      if (!res.ok) { const data = await res.json(); throw new Error(data.error || 'Download mislukt'); }
      const disposition = res.headers.get('Content-Disposition');
      let filename = `render_${item.templateType}.mov`;
      if (disposition) { const match = disposition.match(/filename="([^"]+)"/); if (match) filename = match[1]; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
  };

  const handleDownloadAll = async () => {
    const completedItems = queue.filter(item => item.status === 'completed');
    for (let i = 0; i < completedItems.length; i++) {
      if (i > 0) await new Promise(r => setTimeout(r, 500));
      await handleDownloadItem(completedItems[i]);
    }
    toast.success('Alle downloads gestart!');
  };

  const handleDirectMovDownload = async () => {
    if (!selectedTemplate || !selectedKlant || selectedFormats.length === 0) return;
    setIsDownloading(true);
    setError(null);
    let successCount = 0;
    for (let i = 0; i < selectedFormats.length; i++) {
      const format = selectedFormats[i];
      try {
        const genRes = await authFetch(`${BACKEND_URL}/Api/name-title/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ templateType: selectedType.key, huisstijlElementId: selectedTemplate.id, format, klantName: selectedKlant?.fields?.Account || '', accountAirtableId: selectedKlant?.id || null, referrerPath: window.location.pathname + window.location.search }) });
        const genData = await genRes.json();
        if (!genRes.ok) throw new Error(genData.error || 'Ophalen mislukt');
        const dlRes = await authFetch(`${BACKEND_URL}/Api/name-title/download/${genData.jobId}`);
        if (!dlRes.ok) { const dlData = await dlRes.json(); throw new Error(dlData.error || 'Download mislukt'); }
        const disposition = dlRes.headers.get('Content-Disposition');
        let filename = `${selectedType.key}_${format}.mov`;
        if (disposition) { const match = disposition.match(/filename="([^"]+)"/); if (match) filename = match[1]; }
        const blob = await dlRes.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        successCount++;
        if (i < selectedFormats.length - 1) await new Promise(r => setTimeout(r, 500));
      } catch (err) { toast.error(`${format}: ${err.message}`); }
    }
    setIsDownloading(false);
    if (successCount > 0) toast.success(`${successCount} ${successCount === 1 ? 'bestand' : 'bestanden'} gedownload`);
  };

  const handleCancelRendering = () => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    setIsRendering(false);
    setQueue(prev => prev.map(item => item.status === 'completed' ? item : { ...item, status: 'queued', jobId: null, progress: 0, progressMessage: '', errorMessage: undefined }));
  };

  const handleRetryItem = async (itemId) => {
    const item = queue.find(i => i.id === itemId);
    if (!item || !selectedTemplate || !selectedKlant) return;
    setQueue(prev => prev.map(i => i.id === itemId ? { ...i, status: 'pending', jobId: null, progress: 0, progressMessage: 'Opnieuw indienen...', errorMessage: undefined } : i));
    try {
      const body = { templateType: item.templateType, huisstijlElementId: selectedTemplate.id, format: item.format, klantName: selectedKlant?.fields?.Account || '', fontElementIds: selectedTemplate.linkedFontIds || [] };
      if (item.compNameOverride) body.compNameOverride = item.compNameOverride;
      if (item.templateType === 'Naamtitel') { body.name = item.name; body.position = item.position || 'L'; body.metFunctie = item.metFunctie !== false; if (item.metFunctie !== false) body.functionTitle = item.functionTitle; }
      else { if (item.title) body.title = item.title; if (item.subtitle) body.subtitle = item.subtitle; if (item.url) body.url = item.url; }
      const res = await authFetch(`${BACKEND_URL}/Api/name-title/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Genereren mislukt');
      setQueue(prev => prev.map(i => i.id === itemId ? { ...i, jobId: data.jobId, status: 'pending', progressMessage: 'Wachten op render server...' } : i));
      if (!pollingRef.current) {
        setIsRendering(true);
        if (!elapsedRef.current) elapsedRef.current = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
        pollingRef.current = setInterval(() => pollAllJobs(queueRef.current), 1000);
      }
      toast.success('Item opnieuw ingediend');
    } catch (err) {
      setQueue(prev => prev.map(i => i.id === itemId ? { ...i, status: 'error', errorMessage: err.message } : i));
      toast.error(`Retry mislukt: ${err.message}`);
    }
  };

  const handleNewQueue = async () => {
    if (queue.length > 0) {
      if (!(await bevestig({ titel: 'Nieuwe wachtrij beginnen?', tekst: `De huidige wachtrij (${queue.length} ${queue.length === 1 ? 'item' : 'items'}) wordt geleegd.`, knop: 'Nieuwe wachtrij', gevaarlijk: true }))) return;
    }
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    setQueue([]); setIsRendering(false); setName(''); setFunctionTitle(''); setTitleText(''); setSubtitleText(''); setUrlText('');
    setPosition('L'); setMetFunctie(true); setSelectedFormats([]); setError(null); setElapsedSeconds(0); setSelectedType(null); setSelectedTemplate(null);
  };

  const getBeeldmerkUrl = (klant) => {
    const beeldmerk = klant.fields?.Beeldmerk;
    if (Array.isArray(beeldmerk) && beeldmerk.length > 0) return beeldmerk[0].thumbnails?.small?.url || beeldmerk[0].url;
    return null;
  };

  const isAddEnabled = () => {
    if (!selectedType || !selectedTemplate || isRendering) return false;
    if (selectedFormats.length === 0) return false;
    if (selectedTemplate?.isDirectMov) return true;
    const af = availableFieldsForCurrentSelection;
    if (af.includes('Naam') && !name.trim()) return false;
    return true;
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const toggleFormat = (fmt) => setSelectedFormats(prev => prev.includes(fmt) ? prev.filter(f => f !== fmt) : [...prev, fmt]);

  /* ═══════════════════════════════════════════════════════════════════════ */
  const workerPill = (
    <Tooltip
      title={onlineWorkers.length > 0 ? onlineWorkers.map(w => w.workerId).join(', ') : workerOnline === false ? 'Geen actieve workers' : ''}
      arrow
      slotProps={{
        tooltip: { sx: { fontFamily: T.fontBody, fontSize: '0.72rem', bgcolor: T.text, borderRadius: '0.5rem', px: 1.5, py: 0.75 } },
        arrow: { sx: { color: T.text, '&::before': { border: 'none' } } },
      }}
    >
      <Box
        onClick={checkWorkerStatus}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.5,
          borderRadius: '9999px', cursor: 'pointer', transition: 'all 0.15s',
          bgcolor: workerOnline === null ? T.surfaceLow : workerOnline ? T.emeraldBg : T.errorBg,
          border: `1px solid ${workerOnline === null ? 'transparent' : workerOnline ? `${T.emerald}30` : `${T.error}30`}`,
          '&:hover': { opacity: 0.8 },
        }}
      >
        <Box sx={{
          width: 6, height: 6, borderRadius: '50%',
          bgcolor: workerOnline === null ? T.outlineVariant : workerOnline ? T.emerald : T.error,
        }} />
        <Typography sx={{
          fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 600,
          color: workerOnline === null ? T.outline : workerOnline ? '#065f46' : T.error,
          display: { xs: 'none', sm: 'block' },
        }}>
          {workerOnline === null ? 'Checken…' : workerOnline ? `Online (${onlineWorkers.length})` : 'Offline'}
        </Typography>
        <Icon name="refresh" size={14} sx={{ color: workerOnline === null ? T.outline : workerOnline ? '#065f46' : T.error }} />
      </Box>
    </Tooltip>
  );

  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>

      {/* ── Shared header ─────────────────────────────────────────────────── */}
      <PageHeader
        icon="badge"
        title="Titel Generator"
        subtitle="Render After Effects templates direct vanuit de browser"
        accent={{ bg: T.tertiaryContainer, color: T.tertiary }}
        rightActions={workerPill}
      />

      {/* ── Content (full-width, two-column op grote schermen) ────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' }, gap: 3, alignItems: 'start', maxWidth: 1280, mx: 'auto' }}>
        <Box sx={{ minWidth: 0 }}>

      {/* ── Step 1: Klant selecteren ────────────────────────────────────── */}
      <Box sx={{ bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5, mb: 3, boxShadow: T.shadow }}>
        <SectionLabel>Selecteer klant</SectionLabel>
        <Autocomplete
          options={klanten}
          getOptionLabel={(option) => option.fields?.Account || ''}
          value={selectedKlant}
          onChange={(_, value) => setSelectedKlant(value)}
          loading={loadingKlanten}
          disabled={isRendering}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Zoek op klantnaam..."
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.5rem', fontSize: '0.84rem', bgcolor: T.surfaceLow,
                  fontFamily: T.fontBody,
                  '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' },
                  '&.Mui-focused fieldset': { borderColor: T.primary },
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              {getBeeldmerkUrl(option) ? (
                <Box component="img" src={getBeeldmerkUrl(option)} sx={{ width: 28, height: 28, borderRadius: '0.35rem', objectFit: 'contain', bgcolor: T.surfaceLow, p: 0.25, flexShrink: 0 }} />
              ) : (
                <Box sx={{ width: 28, height: 28, borderRadius: '0.35rem', bgcolor: T.surfaceLow, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: T.outline }}>{(option.fields?.Account || '?').substring(0, 2).toUpperCase()}</Typography>
                </Box>
              )}
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', color: T.text }}>{option.fields?.Account}</Typography>
            </Box>
          )}
          noOptionsText="Geen klanten gevonden"
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
      </Box>

      {/* ── Step 2: Template type kaarten ───────────────────────────────── */}
      {selectedKlant && !selectedType && (
        <Box sx={{ bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5, mb: 3, boxShadow: T.shadow }}>
          <SectionLabel>Kies template type</SectionLabel>

          {loadingHuisstijl ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              {[1, 2, 3, 4].map(i => (
                <Box key={i} sx={{ height: 110, borderRadius: '0.75rem', bgcolor: T.surfaceLow, animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />
              ))}
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                {TEMPLATE_TYPES.map((type) => {
                  const templates = aeTemplates[type.key] || [];
                  const available = templates.length > 0;
                  const count = templates.length;
                  const movCount = templates.filter(t => t.isDirectMov).length;
                  const aeCount = count - movCount;
                  const allMov = count > 0 && movCount === count;

                  return (
                    <Box
                      key={type.key}
                      onClick={available ? () => setSelectedType(type) : undefined}
                      sx={{
                        p: 2.5, borderRadius: '0.75rem', textAlign: 'center',
                        cursor: available ? 'pointer' : 'default',
                        bgcolor: available ? T.surface : T.surfaceLow,
                        border: `1.5px solid ${available ? (allMov ? T.violetBorder : `${T.primary}20`) : 'transparent'}`,
                        opacity: available ? 1 : 0.45,
                        transition: 'all 0.15s',
                        '&:hover': available ? {
                          borderColor: allMov ? T.violet : T.primary,
                          boxShadow: '0 8px 24px rgba(45,51,53,0.08)',
                          transform: 'translateY(-2px)',
                        } : {},
                      }}
                    >
                      <IconBadge
                        name={allMov ? 'movie' : type.icon}
                        size={44} iconSize={24}
                        bg={allMov ? T.violetBg : T.primaryContainer}
                        color={allMov ? T.violet : T.primary}
                      />
                      <Typography sx={{ fontFamily: T.font, fontSize: '0.92rem', fontWeight: 700, color: available ? T.text : T.outlineVariant, mt: 1.5, mb: 0.75 }}>
                        {type.label}
                      </Typography>
                      {available ? (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                          {allMov ? (
                            <Badge label={count > 1 ? `${count} video's` : 'Video download'} color={T.violet} bg={T.violetBg} />
                          ) : movCount > 0 ? (
                            <>
                              <Badge label={`${aeCount} template${aeCount > 1 ? 's' : ''}`} color={T.emerald} bg={T.emeraldBg} />
                              <Badge label={`${movCount} video`} color={T.violet} bg={T.violetBg} />
                            </>
                          ) : (
                            <Badge label={count > 1 ? `${count} templates` : 'Beschikbaar'} color={T.emerald} bg={T.emeraldBg} />
                          )}
                        </Box>
                      ) : (
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outlineVariant }}>
                          Niet beschikbaar
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>

              <Box
                onClick={() => navigate(`/accounts/${selectedKlant.id}/workspace`)}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2, cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
              >
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600, color: T.primary }}>
                  Templates beheren
                </Typography>
                <Icon name="open_in_new" size={14} sx={{ color: T.primary }} />
              </Box>
            </>
          )}
        </Box>
      )}

      {/* ── Step 2b: Template kiezen ────────────────────────────────────── */}
      {selectedType && !selectedTemplate && (aeTemplates[selectedType.key] || []).length >= 1 && (() => {
        const allTemplates = [...(aeTemplates[selectedType.key] || [])].sort((a, b) => (b.fields?.['Laatst aangepast op'] || '').localeCompare(a.fields?.['Laatst aangepast op'] || ''));
        const aeItems = allTemplates.filter(t => !t.isDirectMov);
        const movItems = allTemplates.filter(t => t.isDirectMov);

        const renderTemplateCard = (tmpl) => {
          const fileName = tmpl.fields?.Naam || 'Naamloos';
          const bestand = tmpl.fields?.Bestand?.[0];
          const fileInfo = bestand ? bestand.filename : null;
          const lastEditedOn = tmpl.fields?.['Laatst aangepast op'];
          const editDate = lastEditedOn ? new Date(lastEditedOn).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
          const editName = tmpl.fields?.['Laatst aangepast door']?.replace(/@.*$/, '') || null;
          const tmplFormats = (() => {
            const cfgs = tmpl.templateConfigs || [];
            if (cfgs.length > 0 && selectedType) return [...new Set(cfgs.filter(c => c.fields?.Type === selectedType.key).map(c => c.fields?.Formaat).filter(Boolean))];
            return [];
          })();
          const preview = tmpl.fields?.Preview;
          const previewUrl = Array.isArray(preview) && preview.length > 0 ? (preview[0].thumbnails?.large?.url || preview[0].url) : null;

          return (
            <Box
              key={tmpl.id}
              onClick={() => setSelectedTemplate(tmpl)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 2, p: 1.5,
                borderRadius: '0.75rem', cursor: 'pointer',
                bgcolor: T.surface,
                border: `1.5px solid ${tmpl.isDirectMov ? T.violetBorder : `${T.primary}15`}`,
                transition: 'all 0.15s',
                '&:hover': {
                  borderColor: tmpl.isDirectMov ? T.violet : T.primary,
                  boxShadow: '0 4px 16px rgba(45,51,53,0.06)',
                },
              }}
            >
              {previewUrl ? (
                <Box
                  component="img" src={previewUrl} alt={fileName}
                  onClick={(e) => { e.stopPropagation(); setLightboxUrl(previewUrl); }}
                  sx={{
                    width: 120, height: 68, objectFit: 'cover', borderRadius: '0.5rem',
                    border: `1px solid ${T.outlineVariant}30`, flexShrink: 0, cursor: 'zoom-in',
                    '&:hover': { borderColor: T.amber, boxShadow: `0 0 0 1px ${T.amber}` },
                  }}
                />
              ) : bestand?.thumbnails?.small?.url ? (
                <Box component="img" src={bestand.thumbnails.small.url} sx={{ width: 48, height: 48, borderRadius: '0.5rem', objectFit: 'contain', flexShrink: 0 }} />
              ) : (
                <IconBadge name={tmpl.isDirectMov ? 'movie' : selectedType.icon} size={48} iconSize={24} bg={tmpl.isDirectMov ? T.violetBg : T.primaryContainer} color={tmpl.isDirectMov ? T.violet : T.primary} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', fontWeight: 700, color: T.text }}>
                  {fileName}
                </Typography>
                {fileInfo && <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outlineVariant }}>{fileInfo}</Typography>}
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                  {tmplFormats.map(fmt => <Badge key={fmt} label={fmt} color={T.outline} bg={T.surfaceLow} />)}
                  {(editDate || editName) && (
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outlineVariant }}>
                      {editDate}{editName ? ` · ${editName}` : ''}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Icon name={tmpl.isDirectMov ? 'download' : 'play_arrow'} size={22} sx={{ color: tmpl.isDirectMov ? T.violet : T.primary, flexShrink: 0 }} />
            </Box>
          );
        };

        const renderGroup = (items) => <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{items.map(renderTemplateCard)}</Box>;

        const renderGrouped = (items, labelColor = T.outline) => {
          const merken = [...new Set(items.filter(t => t.fields?.Merk).map(t => t.fields.Merk))].sort((a, b) => a.localeCompare(b, 'nl'));
          const zonderMerk = items.filter(t => !t.fields?.Merk);
          if (merken.length === 0) return renderGroup(items);
          return (
            <>
              {zonderMerk.length > 0 && <Box sx={{ mb: 1.5 }}>{renderGroup(zonderMerk)}</Box>}
              {merken.map(m => (
                <Box key={m} sx={{ mb: 1.5 }}>
                  <Badge label={m} color={T.pink} bg={T.pinkBg} sx={{ mb: 1 }} />
                  {renderGroup(items.filter(t => t.fields?.Merk === m))}
                </Box>
              ))}
            </>
          );
        };

        return (
          <Box sx={{ bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5, mb: 3, boxShadow: T.shadow }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box onClick={() => setSelectedType(null)} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', '&:hover': { opacity: 0.7 } }}>
                <Icon name="arrow_back" size={20} sx={{ color: T.primary }} />
              </Box>
              <Typography sx={{ fontFamily: T.font, fontSize: '1rem', fontWeight: 700, color: T.text }}>
                Kies voor {selectedType.label}
              </Typography>
            </Box>

            {aeItems.length > 0 && (
              <>
                {movItems.length > 0 && <SectionLabel>Templates (AE render)</SectionLabel>}
                {renderGrouped(aeItems)}
              </>
            )}

            {movItems.length > 0 && (
              <Box sx={{ mt: aeItems.length > 0 ? 2.5 : 0 }}>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.violet, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                  Video downloads
                </Typography>
                {renderGrouped(movItems)}
              </Box>
            )}
          </Box>
        );
      })()}

      {/* ── Step 3a: Direct MOV download ────────────────────────────────── */}
      {selectedType && selectedTemplate && selectedTemplate.isDirectMov && (
        <Box sx={{ bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5, mb: 3, boxShadow: T.shadow, border: `1.5px solid ${T.violetBorder}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <Box onClick={!isDownloading ? handleNewQueue : undefined} sx={{ cursor: 'pointer', display: 'flex', '&:hover': { opacity: 0.7 } }}>
              <Icon name="arrow_back" size={20} sx={{ color: T.primary }} />
            </Box>
            <Typography sx={{ fontFamily: T.font, fontSize: '1rem', fontWeight: 700, color: T.text }}>
              {selectedType.label}
            </Typography>
            <Badge label={selectedKlant?.fields?.Account} color={T.outline} bg={T.surfaceLow} />
            {(aeTemplates[selectedType.key] || []).length > 1 && (
              <Badge label={selectedTemplate?.fields?.Naam || 'Video'} color={T.violet} bg={T.violetBg} />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, mb: 2, borderRadius: '0.75rem', bgcolor: T.violetBg, border: `1px solid ${T.violetBorder}` }}>
            <Icon name="movie" size={18} sx={{ color: T.violet, marginTop: 1 }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: '#5b21b6', lineHeight: 1.6 }}>
              Dit is een pre-rendered video. Selecteer de formaten en download direct.
            </Typography>
          </Box>

          {availableFormats.length > 0 && (
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <Icon name="aspect_ratio" size={16} sx={{ color: T.secondary }} />
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.text }}>
                  Formaat{selectedFormats.length > 0 ? ` (${selectedFormats.length}/${availableFormats.length})` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {availableFormats.map(fmt => {
                  const sel = selectedFormats.includes(fmt);
                  return (
                    <Box key={fmt} onClick={!isDownloading ? () => toggleFormat(fmt) : undefined} sx={{
                      px: 1.5, py: 0.5, borderRadius: '9999px', cursor: isDownloading ? 'default' : 'pointer',
                      bgcolor: sel ? T.violetBg : T.surfaceLow,
                      border: `1.5px solid ${sel ? T.violet : 'transparent'}`,
                      transition: 'all 0.15s',
                      '&:hover': !isDownloading && !sel ? { bgcolor: T.surfaceHigh } : {},
                    }}>
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: sel ? 700 : 500, color: sel ? T.violet : T.text }}>{fmt}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {availableFormats.length === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, mb: 2, borderRadius: '0.75rem', bgcolor: T.amberBg, border: `1px solid ${T.amber}25` }}>
              <Icon name="warning" size={18} sx={{ color: T.amber }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: '#92400e' }}>
                Geen formaten ingesteld. Voeg formaten toe via de huisstijl pagina.
              </Typography>
            </Box>
          )}

          <GradientButton onClick={handleDirectMovDownload} disabled={selectedFormats.length === 0 || isDownloading} icon={isDownloading ? undefined : 'download'} variant="violet" fullWidth>
            {isDownloading ? <><Spinner /> Downloaden…</> : `Download${selectedFormats.length > 1 ? ` (${selectedFormats.length} formaten)` : ''}`}
          </GradientButton>
        </Box>
      )}

      {/* ── Step 3b: Form + Queue (AE templates) ───────────────────────── */}
      {selectedType && selectedTemplate && !selectedTemplate.isDirectMov && (
        <Box sx={{ bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5, mb: 3, boxShadow: T.shadow }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <Box onClick={!isRendering ? handleNewQueue : undefined} sx={{ cursor: isRendering ? 'default' : 'pointer', display: 'flex', '&:hover': !isRendering ? { opacity: 0.7 } : {} }}>
              <Icon name="arrow_back" size={20} sx={{ color: isRendering ? T.outlineVariant : T.primary }} />
            </Box>
            <Typography sx={{ fontFamily: T.font, fontSize: '1rem', fontWeight: 700, color: T.text }}>
              {selectedType.label}
            </Typography>
            <Badge label={selectedKlant?.fields?.Account} color={T.outline} bg={T.surfaceLow} />
            {(aeTemplates[selectedType.key] || []).length > 1 && (
              <Badge label={selectedTemplate?.fields?.Naam || 'Template'} />
            )}
          </Box>

          {/* Font info */}
          {(() => {
            const fontNames = (selectedTemplate?.linkedFontIds || []).map(id => huisstijl.find(r => r.id === id)?.fields?.Naam).filter(Boolean);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
                <Icon name="font_download" size={16} sx={{ color: fontNames.length > 0 ? T.emerald : T.outlineVariant }} />
                {fontNames.length > 0 ? fontNames.map(n => (
                  <Badge key={n} label={n} color="#065f46" bg={T.emeraldBg} />
                )) : (
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outlineVariant }}>Geen fonts gekoppeld</Typography>
                )}
              </Box>
            );
          })()}

          {/* Form fields */}
          {!isRendering && !allDone && (() => {
            const af = availableFieldsForCurrentSelection;
            const hasNaam = af.includes('Naam');
            const hasFunctie = af.includes('Functie');
            const hasTitel = af.includes('Titel');
            const hasSubtitel = af.includes('Subtitel');
            const hasUrl = af.includes('URL');

            const inputSx = {
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.5rem', fontSize: '0.84rem', bgcolor: T.surfaceLow, fontFamily: T.fontBody,
                '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' },
                '&.Mui-focused fieldset': { borderColor: T.primary },
              },
              '& .MuiInputLabel-root': { fontFamily: T.fontBody, fontSize: '0.84rem' },
            };

            return (
              <>
                {hasNaam && <TextField fullWidth label="Naam" placeholder="Jan Jansen" value={name} onChange={(e) => setName(e.target.value)} inputRef={nameInputRef} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} sx={inputSx} />}

                {hasNaam && selectedType.key === 'Naamtitel' && !configPosition && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                      <Icon name="align_horizontal_left" size={16} sx={{ color: T.secondary }} />
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.text }}>Positie</Typography>
                    </Box>
                    <PillSelect options={[{ value: 'L', label: 'Links' }, { value: 'R', label: 'Rechts' }]} value={position} onChange={setPosition} />
                  </Box>
                )}
                {hasNaam && selectedType.key === 'Naamtitel' && configPosition && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Icon name="align_horizontal_left" size={16} sx={{ color: T.secondary }} />
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.text }}>Positie:</Typography>
                    <Badge label={configPosition === 'L' ? 'Links' : 'Rechts'} />
                  </Box>
                )}

                {hasFunctie && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                        <Icon name="work" size={16} sx={{ color: T.secondary }} />
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.text }}>Functietitel</Typography>
                      </Box>
                      <PillSelect
                        options={[{ value: true, label: 'Met functie' }, { value: false, label: 'Zonder functie' }]}
                        value={metFunctie}
                        onChange={(v) => { setMetFunctie(v); if (!v) setFunctionTitle(''); }}
                      />
                    </Box>
                    {metFunctie && <TextField fullWidth label="Functie" placeholder="Camera Operator" value={functionTitle} onChange={(e) => setFunctionTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && isAddEnabled()) { e.preventDefault(); handleAddToQueue(); } }} sx={inputSx} />}
                  </>
                )}

                {hasTitel && <TextField fullWidth label="Titel" placeholder="Voer een titel in" value={titleText} onChange={(e) => setTitleText(e.target.value)} inputRef={titleInputRef} onKeyDown={(e) => { if (e.key === 'Enter' && isAddEnabled()) { e.preventDefault(); handleAddToQueue(); } }} sx={inputSx} />}
                {hasSubtitel && <TextField fullWidth label="Subtitel" placeholder="Optionele subtitel" value={subtitleText} onChange={(e) => setSubtitleText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && isAddEnabled()) { e.preventDefault(); handleAddToQueue(); } }} sx={inputSx} />}
                {hasUrl && <TextField fullWidth label="URL" placeholder="https://example.com" value={urlText} onChange={(e) => setUrlText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && isAddEnabled()) { e.preventDefault(); handleAddToQueue(); } }} sx={inputSx} />}

                {/* Format selection */}
                {availableFormats.length > 0 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                      <Icon name="aspect_ratio" size={16} sx={{ color: T.secondary }} />
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.text }}>
                        Formaat{selectedFormats.length > 0 ? ` (${selectedFormats.length}/${availableFormats.length})` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {availableFormats.map(fmt => {
                        const sel = selectedFormats.includes(fmt);
                        return (
                          <Box key={fmt} onClick={() => toggleFormat(fmt)} sx={{
                            px: 1.5, py: 0.5, borderRadius: '9999px', cursor: 'pointer',
                            bgcolor: sel ? T.primaryContainer : T.surfaceLow,
                            border: `1.5px solid ${sel ? `${T.primary}40` : 'transparent'}`,
                            transition: 'all 0.15s',
                            '&:hover': !sel ? { bgcolor: T.surfaceHigh } : {},
                          }}>
                            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: sel ? 700 : 500, color: sel ? T.primary : T.text }}>{fmt}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {availableFormats.length === 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, mb: 2, borderRadius: '0.75rem', bgcolor: T.amberBg, border: `1px solid ${T.amber}25` }}>
                    <Icon name="warning" size={18} sx={{ color: T.amber }} />
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: '#92400e' }}>
                      Geen formaten ingesteld voor dit template.
                    </Typography>
                  </Box>
                )}

                {/* Add to queue */}
                <Box
                  onClick={isAddEnabled() ? handleAddToQueue : undefined}
                  sx={{
                    py: 1, borderRadius: '0.5rem', cursor: isAddEnabled() ? 'pointer' : 'default',
                    bgcolor: isAddEnabled() ? 'transparent' : T.surfaceLow,
                    border: `1.5px solid ${isAddEnabled() ? T.primary : T.outlineVariant}40`,
                    color: isAddEnabled() ? T.primary : T.outlineVariant,
                    fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.82rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                    transition: 'all 0.15s',
                    '&:hover': isAddEnabled() ? { bgcolor: `${T.primary}08` } : {},
                  }}
                >
                  <Icon name="add" size={18} sx={{ color: 'inherit' }} />
                  Voeg toe aan queue{selectedFormats.length > 1 ? ` (${selectedFormats.length} formaten)` : ''}
                </Box>
              </>
            );
          })()}

          {/* ── Queue overview ──────────────────────────────────────────── */}
          {queue.length > 0 && (
            <>
              <Box sx={{ height: 1, bgcolor: T.surfaceLow, my: 2.5 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Icon name="queue" size={18} sx={{ color: T.secondary }} />
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 700, color: T.text }}>
                  Queue ({queue.length} {queue.length === 1 ? 'item' : 'items'})
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
                {queue.map((item) => {
                  const statusColors = {
                    completed: { bg: T.emeraldBg, border: `${T.emerald}30`, icon: 'check_circle', iconColor: T.emerald },
                    error: { bg: T.errorBg, border: `${T.error}30`, icon: 'error', iconColor: T.error },
                    rendering: { bg: `${T.tertiary}08`, border: `${T.tertiary}20`, icon: null, iconColor: null },
                    pending: { bg: T.surfaceLow, border: 'transparent', icon: null, iconColor: null },
                    queued: { bg: T.surfaceLow, border: 'transparent', icon: 'hourglass_empty', iconColor: T.outlineVariant },
                  };
                  const sc = statusColors[item.status] || statusColors.queued;

                  return (
                    <Box key={item.id}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5,
                        borderRadius: '0.5rem', bgcolor: sc.bg, border: `1px solid ${sc.border}`,
                      }}>
                        {/* Status icon */}
                        {(item.status === 'rendering' || item.status === 'pending') ? (
                          <Box sx={{
                            width: 18, height: 18, border: `2px solid ${T.outlineVariant}`,
                            borderTop: `2px solid ${item.status === 'rendering' ? T.tertiary : T.outline}`,
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite',
                            '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
                            flexShrink: 0,
                          }} />
                        ) : (
                          <Icon name={sc.icon} size={18} sx={{ color: sc.iconColor, flexShrink: 0 }} />
                        )}

                        {/* Label */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.text }}>
                            {getItemLabel(item)}
                          </Typography>
                          <Typography sx={{
                            fontFamily: T.fontBody, fontSize: '0.68rem', wordBreak: 'break-word',
                            color: item.status === 'error' ? T.error : T.secondary,
                          }}>
                            {item.status === 'queued' && 'Wachtend'}
                            {item.status === 'pending' && (item.isDirectMov ? 'Bestand ophalen…' : item.wachtOpAe ? `Wacht op render-server met After Effects ${item.wachtOpAe}` : 'Wachten op render server…')}
                            {item.status === 'rendering' && `Renderen ${item.progress > 20 ? Math.round((item.progress - 20) / 0.55) + '%' : '…'}`}
                            {item.status === 'completed' && 'Voltooid'}
                            {item.status === 'error' && (item.errorMessage || 'Mislukt')}
                          </Typography>
                        </Box>

                        {/* Actions */}
                        {item.status === 'queued' && !isRendering && (
                          <Box onClick={() => handleRemoveFromQueue(item.id)} sx={{ cursor: 'pointer', display: 'flex', '&:hover': { opacity: 0.7 } }}>
                            <Icon name="delete" size={18} sx={{ color: T.outline }} />
                          </Box>
                        )}
                        {item.status === 'error' && (
                          <Box onClick={() => handleRetryItem(item.id)} sx={{ cursor: 'pointer', display: 'flex', '&:hover': { opacity: 0.7 } }}>
                            <Icon name="replay" size={18} sx={{ color: T.amber }} />
                          </Box>
                        )}
                        {item.status === 'completed' && (
                          <Box onClick={() => handleDownloadItem(item)} sx={{ cursor: 'pointer', display: 'flex', '&:hover': { opacity: 0.7 } }}>
                            <Icon name="download" size={18} sx={{ color: T.emerald }} />
                          </Box>
                        )}
                      </Box>

                      {/* Render steps */}
                      {(item.status === 'rendering' || item.status === 'pending') && isRendering && item.jobId && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, ml: 4, mt: 0.5 }}>
                          {(item.isDirectMov
                            ? [{ label: 'Bestand ophalen', done: item.status === 'completed', active: item.status !== 'completed' }]
                            : [
                                { label: 'In wachtrij', done: item.progress > 0 },
                                { label: 'Template downloaden', done: item.progress >= 15 },
                                { label: 'After Effects renderen', done: item.progress >= 75, active: item.progress >= 15 && item.progress < 75 },
                              ]
                          ).map((step, i, steps) => {
                            const isActive = step.active || (!step.done && (i === 0 || steps[i - 1].done));
                            return (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                {step.done ? (
                                  <Icon name="check_circle" size={14} sx={{ color: T.emerald }} />
                                ) : isActive ? (
                                  <Box sx={{ width: 12, height: 12, border: `2px solid ${T.outlineVariant}`, borderTop: `2px solid ${T.tertiary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
                                ) : (
                                  <Icon name="radio_button_unchecked" size={14} sx={{ color: T.outlineVariant }} />
                                )}
                                <Typography sx={{
                                  fontFamily: T.fontBody, fontSize: '0.68rem',
                                  color: step.done ? T.secondary : isActive ? T.text : T.outlineVariant,
                                  fontWeight: isActive ? 700 : 400,
                                }}>
                                  {step.label}
                                  {isActive && step.label === 'After Effects renderen' && item.progress > 20 ? ` (${Math.round((item.progress - 20) / 0.55)}%)` : ''}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* Elapsed time */}
              {(isRendering || allDone) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                  <Icon name="schedule" size={14} sx={{ color: T.outlineVariant }} />
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outlineVariant }}>
                    Verstreken: {formatTime(elapsedSeconds)}
                  </Typography>
                </Box>
              )}

              {/* Worker offline warning */}
              {!workerOnline && !isRendering && !allDone && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, mb: 2, borderRadius: '0.75rem', bgcolor: T.amberBg, border: `1px solid ${T.amber}25` }}>
                  <Icon name="warning" size={18} sx={{ color: T.amber, marginTop: 1 }} />
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: '#92400e', lineHeight: 1.6 }}>
                    De render server is offline. Controleer of de Mac op kantoor aan staat en het worker script draait.
                  </Typography>
                </Box>
              )}

              {/* Action buttons */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                {!isRendering && !allDone && (
                  <GradientButton onClick={handleStartRendering} disabled={queue.length === 0 || !workerOnline} icon="play_arrow" fullWidth>
                    Start renderen ({queue.length})
                  </GradientButton>
                )}

                {isRendering && (
                  <>
                    <Box sx={{
                      flex: 1, py: 1.25, borderRadius: '0.75rem',
                      bgcolor: T.surfaceHigh, color: T.outline,
                      fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.85rem',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                    }}>
                      <Box sx={{ width: 16, height: 16, border: `2px solid ${T.outlineVariant}`, borderTop: `2px solid ${T.tertiary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
                      Renderen…
                    </Box>
                    <Box
                      onClick={handleCancelRendering}
                      sx={{
                        px: 2.5, py: 1.25, borderRadius: '0.75rem', cursor: 'pointer',
                        border: `1.5px solid ${T.error}40`, color: T.error,
                        fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.85rem',
                        display: 'flex', alignItems: 'center', gap: 0.75,
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: T.errorBg },
                      }}
                    >
                      <Icon name="cancel" size={18} sx={{ color: T.error }} />
                      Annuleer
                    </Box>
                  </>
                )}

                {allDone && !isRendering && (
                  <>
                    {hasCompletedItems && (
                      <GradientButton onClick={handleDownloadAll} icon="download" variant="emerald" sx={{ flex: 1 }}>
                        Download alle ({queue.filter(i => i.status === 'completed').length})
                      </GradientButton>
                    )}
                    <Box
                      onClick={handleNewQueue}
                      sx={{
                        px: 2.5, py: 1.25, borderRadius: '0.75rem', cursor: 'pointer',
                        border: `1.5px solid ${T.outlineVariant}40`, color: T.text,
                        fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.85rem',
                        display: 'flex', alignItems: 'center', gap: 0.75,
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: T.surfaceLow },
                      }}
                    >
                      <Icon name="refresh" size={18} sx={{ color: T.outline }} />
                      Nieuwe queue
                    </Box>
                  </>
                )}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* ── Error ───────────────────────────────────────────────────────── */}
      {error && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, p: 2, mb: 3,
          borderRadius: '0.75rem', bgcolor: T.errorBg, border: `1px solid ${T.error}20`,
        }}>
          <Icon name="error" size={18} sx={{ color: T.error }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.error, flex: 1 }}>{error}</Typography>
          <Box onClick={() => setError(null)} sx={{ cursor: 'pointer', display: 'flex', '&:hover': { opacity: 0.7 } }}>
            <Icon name="close" size={16} sx={{ color: T.error }} />
          </Box>
        </Box>
      )}

        {/* einde hoofdkolom */}
        </Box>

        {/* ── Info (zijbalk) ─────────────────────────────────────────────── */}
        <Box sx={{ position: { lg: 'sticky' }, top: 24 }}>
          <Box sx={{ bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5, boxShadow: T.shadow }}>
            <SectionLabel>Over deze tool</SectionLabel>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary, lineHeight: 1.6 }}>
              Deze tool genereert video titels met transparante achtergrond (ProRes 4444) vanuit de
              huisstijl templates van de geselecteerde klant. De output kan direct in je video editor
              worden gebruikt als overlay.
            </Typography>
          </Box>
        </Box>
      {/* einde content grid */}
      </Box>

      {/* ── Preview lightbox ─────────────────────────────────────────────── */}
      <Dialog
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        maxWidth={false}
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' }, onClick: () => setLightboxUrl(null) }}
      >
        {lightboxUrl && (
          <Box component="img" src={lightboxUrl} alt="Preview" sx={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '0.75rem', cursor: 'pointer' }} />
        )}
      </Dialog>
    </Box>
  );
}
